import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { StatusBadge } from "../../../components/ui/StatusBadge";
import { ALL_STATUSES } from "../../../utils/orderStatus";
import { formatDateTime } from "../../../utils/dates";

/**
 * Vertical status timeline for an order's `status_history`.
 * - When cancelled: renders the completed transitions in order, then a
 *   terminal red "Cancelled" node with optional reason.
 * - Otherwise: renders each transition, marks the last one as "current",
 *   and appends greyed-out future steps up to `collection_complete`.
 */
export function StatusTimeline({ order }) {
  const { t, i18n } = useTranslation();

  const { entries, futureSteps, cancelledEntry } = useMemo(() => {
    // A18.3 — "draft" is not part of an order's public status history.
    // Filter every draft entry out before rendering so submitted orders
    // consistently start at `order_placed`, even when the historical
    // rows still carry legacy draft entries the migration hasn't
    // scrubbed yet.
    const history = (order?.status_history || []).filter(
      (h) => h?.status !== "draft"
    );
    const isCancelled = order?.status === "cancelled";
    if (isCancelled) {
      const cancelIdx = history.findIndex((h) => h.status === "cancelled");
      const before =
        cancelIdx >= 0 ? history.slice(0, cancelIdx) : history.slice();
      const cancel = cancelIdx >= 0 ? history[cancelIdx] : null;
      return {
        entries: before,
        futureSteps: [],
        cancelledEntry: cancel,
      };
    }
    const currentIdx = ALL_STATUSES.indexOf(order?.status);
    const future =
      currentIdx >= 0 ? ALL_STATUSES.slice(currentIdx + 1) : [];
    return { entries: history, futureSteps: future, cancelledEntry: null };
  }, [order]);

  return (
    <div
      className="rounded-lg border border-border-subtle bg-surface p-5 shadow-card"
      data-testid="status-timeline"
    >
      <div className="mb-4 flex items-baseline justify-between">
        <h3 className="text-sm font-semibold text-text-primary">
          {t("orders.detail.timeline_heading")}
        </h3>
        <div className="h-px w-6 bg-champagne" aria-hidden />
      </div>

      <ol className="relative space-y-4">
        <span
          aria-hidden
          className="absolute left-[7px] top-1 bottom-1 w-px bg-border-subtle"
        />

        {entries.map((h, i) => {
          const isLast = i === entries.length - 1 && !cancelledEntry;
          return (
            <TimelineNode
              key={`${h.status}-${h.at}-${i}`}
              status={h.status}
              at={h.at}
              note={h.note}
              lang={i18n.language}
              variant={isLast ? "current" : "done"}
              label={
                isLast ? t("orders.detail.timeline_current") : undefined
              }
            />
          );
        })}

        {cancelledEntry && (
          <TimelineNode
            key="cancelled"
            status="cancelled"
            at={cancelledEntry.at}
            note={cancelledEntry.note}
            lang={i18n.language}
            variant="cancelled"
            label={t("orders.detail.timeline_cancelled_at", {
              date: formatDateTime(cancelledEntry.at, i18n.language),
            })}
          />
        )}

        {futureSteps.map((s) => (
          <TimelineNode
            key={`future-${s}`}
            status={s}
            variant="future"
            label={t("orders.detail.timeline_future")}
            lang={i18n.language}
          />
        ))}
      </ol>
    </div>
  );
}

function TimelineNode({ status, at, note, lang, variant, label }) {
  const dotClasses = {
    done: "bg-champagne border-champagne",
    current: "bg-champagne border-champagne ring-2 ring-champagne/40",
    future: "bg-surface border-border-subtle",
    cancelled: "bg-status-unavailable border-status-unavailable",
  }[variant];

  const rowOpacity = variant === "future" ? "opacity-55" : "";

  return (
    <li
      className={`relative flex gap-3 pl-6 ${rowOpacity}`}
      data-testid={`timeline-node-${status}`}
      data-variant={variant}
    >
      <span
        aria-hidden
        className={`absolute left-0 top-1 h-4 w-4 rounded-full border ${dotClasses}`}
      />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge status={status} size="sm" />
          {at && (
            <span className="text-[11px] text-text-secondary">
              {formatDateTime(at, lang)}
            </span>
          )}
          {label && !at && (
            <span className="text-[11px] uppercase tracking-wide text-text-secondary">
              {label}
            </span>
          )}
        </div>
        {note && (
          <p className="mt-1 text-xs text-text-secondary">{note}</p>
        )}
      </div>
    </li>
  );
}
